// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "onForest";
type Size = "default" | "lg";

interface MarketingButtonBaseProps {
  variant?: Variant;
  size?: Size;
  withArrow?: boolean;
  className?: string;
  children: React.ReactNode;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    "bg-marketing-ink text-marketing-canvas hover:bg-marketing-accentHover shadow-sm shadow-[hsl(262_40%_66%/0.12)]",
  secondary:
    "border border-marketing-hairline bg-transparent text-marketing-ink hover:bg-marketing-surfaceHover",
  ghost:
    "bg-transparent text-marketing-inkSecondary hover:bg-marketing-surfaceHover hover:text-marketing-ink",
  onForest:
    "bg-marketing-ink text-marketing-canvas hover:bg-marketing-inkSecondary",
};

const SIZE_CLASSES: Record<Size, string> = {
  default: "px-5 py-2.5 text-sm",
  lg: "px-6 py-3 text-[15px]",
};

interface MarketingLinkButtonProps
  extends MarketingButtonBaseProps,
    Omit<React.ComponentPropsWithoutRef<typeof Link>, "to" | "children"> {
  to: string;
  type?: never;
  onClick?: never;
}

interface MarketingNativeButtonProps
  extends MarketingButtonBaseProps,
    Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "type" | "children" | "className"
    > {
  to?: undefined;
  type?: "button" | "submit" | "reset";
}

type Props = MarketingLinkButtonProps | MarketingNativeButtonProps;

export const MarketingButton = forwardRef<HTMLElement, Props>((props, ref) => {
  const {
    variant = "primary",
    size = "default",
    withArrow = false,
    className,
    children,
  } = props;

  const classes = cn(
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium tracking-[-0.005em] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-marketing-accent focus-visible:ring-offset-2 focus-visible:ring-offset-marketing-canvas",
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    className,
  );

  const body = (
    <>
      {children}
      {withArrow && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
    </>
  );

  if ("to" in props && props.to) {
    const { to, withArrow: _withArrow, variant: _variant, size: _size, className: _className, children: _children, ...rest } = props;
    return (
      <Link
        {...rest}
        to={to}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={classes}
      >
        {body}
      </Link>
    );
  }

  const { withArrow: _withArrow, variant: _variant, size: _size, className: _className, children: _children, ...rest } = props as MarketingNativeButtonProps;
  return (
    <button
      {...rest}
      ref={ref as React.Ref<HTMLButtonElement>}
      type={rest.type ?? "button"}
      className={classes}
    >
      {body}
    </button>
  );
});

MarketingButton.displayName = "MarketingButton";

export default MarketingButton;
